import { useEffect, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHubspot } from "@fortawesome/free-brands-svg-icons";
import { useHubSpotSearch } from "../hooks/useHubSpot";
import { HUBSPOT_ORANGE } from "../utils/hubspot";
import type { HubSpotContactSuggestion } from "@event-planner/shared";

/**
 * The create-page email field with CRM suggestions (ADR-169 Stage 2).
 * As the organizer types a name or email, matching HubSpot contacts
 * surface beneath the input (debounced 300ms, min 3 chars, server-side
 * search via the searchHubSpotContacts callable). Picking one is how a
 * person is born linked — the page stashes the contactId and prefills
 * empty form fields. Never auto-picks: free typing always works, and an
 * unpicked suggestion changes nothing.
 *
 * Each option shows the firm on the secondary line so same-name people
 * at different firms are distinguishable, and carries a HubSpot chip so
 * CRM suggestions can't be confused with the duplicate-email warning
 * (which means the opposite: the person already exists HERE).
 */

interface HubSpotContactTypeaheadProps {
  /** Controlled email input value (owned by the page). */
  value: string;
  /**
   * Input text changed. `reason` is MUI's change reason — the page
   * clears its stashed link on "input" (manual edits) but not on the
   * "reset" fired by a pick.
   */
  onChange: (next: string, reason: string) => void;
  onPick: (suggestion: HubSpotContactSuggestion) => void;
  disabled?: boolean;
}

export function HubSpotContactTypeahead({
  value,
  onChange,
  onPick,
  disabled,
}: HubSpotContactTypeaheadProps) {
  const { search } = useHubSpotSearch();
  const [options, setOptions] = useState<HubSpotContactSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against out-of-order responses: only the latest request's
  // results may land (same pattern as LocationPicker's generation counter).
  const generationRef = useRef(0);

  useEffect(
    () => () => {
      generationRef.current += 1;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  function handleInput(next: string, reason: string) {
    onChange(next, reason);
    const generation = ++generationRef.current;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(false);
    if (reason !== "input") return;
    // Never leave suggestions for the previous query clickable while a new
    // debounce/request is pending.
    setOptions([]);
    const trimmed = next.trim();
    if (trimmed.length < 3) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      search(trimmed)
        .then((response) => {
          if (generationRef.current === generation) {
            setOptions(response.results);
          }
        })
        .catch(() => {
          // Search failures (rate limit, network) just mean no
          // suggestions — typing an email by hand always works.
          if (generationRef.current === generation) setOptions([]);
        })
        .finally(() => {
          if (generationRef.current === generation) setLoading(false);
        });
    }, 300);
  }

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={options}
      // Server-side matching — never re-filter locally (name queries
      // wouldn't substring-match the email labels).
      filterOptions={(opts) => opts}
      getOptionLabel={(opt) =>
        typeof opt === "string" ? opt : (opt.email ?? "")
      }
      inputValue={value}
      onInputChange={(_, next, reason) => handleInput(next, reason)}
      onChange={(_, picked) => {
        if (picked && typeof picked !== "string") onPick(picked);
      }}
      renderOption={(props, opt) => {
        const name =
          [opt.firstName, opt.lastName].filter(Boolean).join(" ") ||
          opt.email ||
          "Unknown";
        const secondary = [opt.email, opt.company]
          .filter(Boolean)
          .join(" · ");
        return (
          <li {...props} key={opt.contactId}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontWeight: 500 }}>{name}</Box>
                <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  {secondary}
                </Box>
              </Box>
              <Chip
                icon={
                  <FontAwesomeIcon
                    icon={faHubspot}
                    style={{ fontSize: 12, color: HUBSPOT_ORANGE }}
                  />
                }
                label="HubSpot"
                size="small"
                variant="outlined"
                sx={{ ml: "auto" }}
              />
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Email"
          type="email"
          required
          helperText="Type a name or email — matching HubSpot contacts appear below."
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            htmlInput: {
              ...params.inputProps,
              "data-testid": "user-create-email-input",
            },
          }}
        />
      )}
    />
  );
}
